import { z } from 'zod';

/**
 * Vite exposes only `VITE_*` env vars to the browser bundle.
 * We validate them once on startup (see `main.tsx`) so a missing/bad var
 * fails loudly at boot instead of at first feature usage.
 */
const EnvSchema = z.object({
  /**
   * Where the API lives — or empty for same-origin.
   *
   * Empty is the PRODUCTION shape, not a fallback. Tenants are identified by
   * subdomain (`acme.example.com` -> tenant `acme`, see the server's
   * `extractSlugFromHostname`), so one built bundle is served from an
   * unbounded set of hostnames and cannot carry any single absolute API url.
   * `buildUrl` in api-client.ts always prefixes `/api`, so an empty base
   * yields a relative `/api/...` that resolves against whatever host the user
   * actually loaded — which is the point: the browser never makes a
   * cross-origin request, so CORS never applies and a new tenant needs no
   * configuration anywhere.
   *
   * That matters because `CORS_ORIGIN` on the server is an exact-match list
   * and refuses `*` in production. With a split app/api origin, every new
   * tenant is a new origin and would need the API redeployed to admit it.
   *
   * An absolute url is still valid, for a preview deployment or a local build
   * pointed at a remote API. `.url()` alone would reject the empty string, so
   * the two cases are spelled out rather than relaxed to `z.string()` — a
   * typo'd non-url must still fail loudly at boot.
   */
  VITE_API_URL: z.union([
    z.literal(''),
    z
      .string()
      .url()
      // `.url()` alone is weaker than it looks: it defers to the WHATWG URL
      // parser, which happily accepts `localhost:3000` (scheme "localhost:",
      // path "3000") and every other scheme-shaped string. That would sail
      // through boot validation and then issue requests to a url the browser
      // cannot fetch. An API base is http(s) or it is nothing.
      .refine((v) => /^https?:\/\//i.test(v), {
        message: 'VITE_API_URL must start with http:// or https:// (or be empty for same-origin)',
      }),
  ]),
  VITE_APP_NAME: z.string().min(1).default('HRMS-SaaS'),
});

export type Env = z.infer<typeof EnvSchema>;

/** Exported for tests only — the schema itself, without the module-level cache. */
export const __EnvSchemaForTests = EnvSchema;

let cached: Env | undefined;

/**
 * Parses `import.meta.env` against the schema. Throws `ZodError` on failure.
 * Call once at entry (main.tsx). Subsequent calls return the cached value.
 */
export function loadEnv(): Env {
  if (cached) return cached;
  cached = EnvSchema.parse(import.meta.env);
  return cached;
}

/**
 * Convenience accessor — assumes `loadEnv()` has been called already.
 * Prefer this at call sites so we don't re-parse on every read.
 */
export const env: Env = new Proxy({} as Env, {
  get(_target, prop: string) {
    const value = loadEnv();
    return value[prop as keyof Env];
  },
});
